import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './AdvertisersSimplePage.scss';

const API_BASE_URL = 'https://api.display-promo.ru';

const AdvertisersSimplePage = () => {
    const { hash } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [modalMedia, setModalMedia] = useState(null);

    useEffect(() => {
        if (!hash) return;

        const source = axios.CancelToken.source();

        setLoading(true);
        setError(null);

        axios
            .get(`${API_BASE_URL}/api/advertisers/${hash}`, {
                cancelToken: source.token,
            })
            .then((response) => {
                setData(response.data);
            })
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                const message =
                    e.response?.data?.error ||
                    e.response?.data?.message ||
                    e.message ||
                    'Ошибка запроса';
                setError(message);
            })
            .finally(() => {
                setLoading(false);
            });

        return () => {
            source.cancel('Component unmounted');
        };
    }, [hash]);

    useEffect(() => {
        if (!modalMedia) return;

        const handleKey = (e) => {
            if (e.key === 'Escape') {
                setModalMedia(null);
            }
            if (e.key === 'ArrowLeft') {
                setModalMedia((prev) => {
                    if (!prev || !prev.reports || prev.reports.length === 0) return prev;
                    const len = prev.reports.length;
                    const nextIndex = (prev.index - 1 + len) % len;
                    return { ...prev, index: nextIndex };
                });
            }
            if (e.key === 'ArrowRight') {
                setModalMedia((prev) => {
                    if (!prev || !prev.reports || prev.reports.length === 0) return prev;
                    const len = prev.reports.length;
                    const nextIndex = (prev.index + 1) % len;
                    return { ...prev, index: nextIndex };
                });
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [modalMedia]);

    const dateLabel = data?.date
        ? new Date(data.date).toLocaleDateString('ru-RU')
        : null;

    const campaignTitle = data?.adFile?.title || 'Компания';

    const placementsRaw = data?.placements || [];

    // Группируем размещения по адресу+городу, чтобы убрать дубли
    const groupedPlacementsRaw = useMemo(() => {
        const map = new Map();

        placementsRaw.forEach((p) => {
            const addr = p.screenAddress || '';
            const city = p.city || '';
            const key = `${addr}|||${city}`;

            const existing = map.get(key);
            if (!existing) {
                map.set(key, {
                    ...p,
                    reports: [...(p.reports || [])],
                    plays: [...(p.plays || [])],
                });
            } else {
                existing.online = existing.online || p.online;

                if (p.lastActive) {
                    if (!existing.lastActive) {
                        existing.lastActive = p.lastActive;
                    } else {
                        const ex = new Date(existing.lastActive).getTime();
                        const cur = new Date(p.lastActive).getTime();
                        if (!Number.isNaN(cur) && cur > ex) {
                            existing.lastActive = p.lastActive;
                        }
                    }
                }

                if (p.lastPlayAt) {
                    if (!existing.lastPlayAt) {
                        existing.lastPlayAt = p.lastPlayAt;
                    } else {
                        const ex = new Date(existing.lastPlayAt).getTime();
                        const cur = new Date(p.lastPlayAt).getTime();
                        if (!Number.isNaN(cur) && cur > ex) {
                            existing.lastPlayAt = p.lastPlayAt;
                        }
                    }
                }

                existing.reports = [
                    ...existing.reports,
                    ...(p.reports || []),
                ];
                existing.plays = [
                    ...existing.plays,
                    ...(p.plays || []),
                ];
            }
        });

        const result = Array.from(map.values());

        result.forEach((item) => {
            if (item.reports?.length) {
                item.reports = [...item.reports].sort((a, b) => {
                    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return tb - ta;
                });
            }
            if (item.plays?.length) {
                item.plays = [...item.plays].sort((a, b) => {
                    const ta = a.playedAt ? new Date(a.playedAt).getTime() : 0;
                    const tb = b.playedAt ? new Date(b.playedAt).getTime() : 0;
                    return tb - ta;
                });
            }
        });

        return result;
    }, [placementsRaw]);

    const placements = useMemo(() => {
        let list = groupedPlacementsRaw.slice();

        if (searchQuery.trim() !== '') {
            const q = searchQuery.trim().toLowerCase();
            list = list.filter((p) => {
                const address = (p.screenAddress || '').toLowerCase();
                const city = (p.city || '').toLowerCase();
                const title = (p.screenTitle || '').toLowerCase();
                return (
                    address.includes(q) ||
                    city.includes(q) ||
                    title.includes(q)
                );
            });
        }

        // Сортируем по городу и адресу для стабильного порядка
        list.sort((a, b) => {
            const ac = (a.city || '').toLowerCase();
            const bc = (b.city || '').toLowerCase();
            if (ac < bc) return -1;
            if (ac > bc) return 1;
            const aa = (a.screenAddress || '').toLowerCase();
            const ba = (b.screenAddress || '').toLowerCase();
            if (aa < ba) return -1;
            if (aa > ba) return 1;
            return 0;
        });

        return list;
    }, [groupedPlacementsRaw, searchQuery]);

    if (!hash) {
        return (
            <div className="advertisers-page">
                <div className="shell">
                    <header>
                        <h1>
                            Мониторинг показов
                            <span>панель рекламодателя</span>
                        </h1>
                        <p>Отсутствует идентификатор кампании в URL.</p>
                    </header>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="advertisers-page">
                <div className="shell">
                    <header>
                        <h1>
                            Мониторинг показов
                            <span>панель рекламодателя</span>
                        </h1>
                        <p>Загружаем данные по вашей кампании…</p>
                        <p className="campaign-hash">
                            ID кампании: <strong>{hash}</strong>
                        </p>
                    </header>
                    <main className="layout">
                        <section className="content">
                            <div className="content-head">
                                <div className="content-head-title">Список адресов</div>
                                <div className="content-head-meta">
                                    <span>Дата: —</span>
                                    <span className="badge">Компания загружается…</span>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="advertisers-page">
                <div className="shell">
                    <header>
                        <h1>
                            Мониторинг показов
                            <span>панель рекламодателя</span>
                        </h1>
                        <p>Ошибка загрузки данных кампании.</p>
                        <p className="campaign-hash">
                            ID кампании: <strong>{hash}</strong>
                        </p>
                    </header>
                    <main className="layout">
                        <section className="content">
                            <div className="content-head">
                                <div className="content-head-title">Список адресов</div>
                                <div className="content-head-meta">
                                    <span>Дата: —</span>
                                    <span className="badge badge-error">
                                        Ошибка: {error}
                                    </span>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        );
    }

    const handleMediaClick = (reportIndex, placement) => {
        setModalMedia({
            placementId: placement.id,
            screenAddress: placement.screenAddress,
            city: placement.city,
            index: reportIndex,
            reports: placement.reports || [],
        });
    };

    const closeModal = () => setModalMedia(null);

    const handlePrevReport = () => {
        setModalMedia((prev) => {
            if (!prev || !prev.reports || prev.reports.length === 0) return prev;
            const len = prev.reports.length;
            const nextIndex = (prev.index - 1 + len) % len;
            return { ...prev, index: nextIndex };
        });
    };

    const handleNextReport = () => {
        setModalMedia((prev) => {
            if (!prev || !prev.reports || prev.reports.length === 0) return prev;
            const len = prev.reports.length;
            const nextIndex = (prev.index + 1) % len;
            return { ...prev, index: nextIndex };
        });
    };

    const currentReport =
        modalMedia && modalMedia.reports && modalMedia.reports.length
            ? modalMedia.reports[modalMedia.index]
            : null;

    return (
        <div className="advertisers-page">
            <div className="shell">
                <header>
                    <h1>
                        Мониторинг показов
                        <span>панель рекламодателя</span>
                    </h1>
                    <p>
                        Выберите адрес экрана в списке ниже и смотрите фотоотчёт
                        по вашей кампании.
                    </p>
                    {hash && (
                        <p className="campaign-hash">
                            ID кампании: <strong>{hash}</strong>
                        </p>
                    )}
                </header>

                <main className="layout">
                    <section className="content">
                        <div className="content-head">
                            <div className="content-head-title">Список адресов</div>
                            <div className="content-head-meta">
                                <span>Дата: {dateLabel || '—'}</span>
                                <span className="badge">
                                    Компания: {campaignTitle}
                                    {hash ? ` (${hash})` : ''}
                                </span>
                            </div>
                        </div>

                        <div className="content-filters">
                            <div className="content-filters-left">
                                <input
                                    type="text"
                                    className="filter-input"
                                    placeholder="Поиск по адресу, городу или экрану"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="screens">
                            {placements.length === 0 && (
                                <div className="screen screen-empty">
                                    <div className="screen-body">
                                        <p>
                                            Для данной кампании нет размещений или загруженных
                                            фото/видео отчётов.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {placements.map((placement, index) => {
                                const reports = placement.reports || [];

                                return (
                                    <details
                                        key={
                                            placement.id ||
                                            `${placement.screenAddress}-${placement.city}`
                                        }
                                        className="screen"
                                        open={index === 0}
                                    >
                                        <summary>
                                            <div className="screen-summary">
                                                <div className="screen-info">
                                                    <div className="screen-address">
                                                        <span className="screen-address-line">
                                                            {placement.screenAddress}
                                                            {placement.city
                                                                ? `, ${placement.city}`
                                                                : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="screen-meta">
                                                    <div className="chevron">⌄</div>
                                                </div>
                                            </div>
                                        </summary>

                                        <div className="screen-body">
                                            <div className="section-block">
                                                <h4>
                                                    Фотоотчёт
                                                    <span>подтверждение запуска вашей кампании</span>
                                                </h4>

                                                {reports.length > 0 ? (
                                                    <div className="photo-grid">
                                                        {reports.map((report, reportIndex) => (
                                                            <article
                                                                key={report.id}
                                                                className="photo-card"
                                                                onClick={() =>
                                                                    handleMediaClick(
                                                                        reportIndex,
                                                                        placement,
                                                                    )
                                                                }
                                                            >
                                                                <div className="photo-card-inner">
                                                                    {report.type === 'video' ? (
                                                                        <video
                                                                            src={report.url}
                                                                            preload="metadata"
                                                                        />
                                                                    ) : (
                                                                        <img
                                                                            src={report.url}
                                                                            alt="Кадр фотоотчёта"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </article>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="photo-hint">
                                                        Для этого экрана ещё не загружены файлы
                                                        фото/видео отчёта.
                                                    </p>
                                                )}

                                                {reports.length > 0 && (
                                                    <p className="photo-hint">
                                                        Кликните по кадру, чтобы открыть отчёт и
                                                        пролистать кадры по этому адресу.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </details>
                                );
                            })}
                        </div>
                    </section>
                </main>
            </div>

            {modalMedia && currentReport && (
                <div className="media-modal" role="dialog" aria-modal="true">
                    <div
                        className="media-modal__backdrop"
                        onClick={closeModal}
                    />
                    <div className="media-modal__dialog">
                        <button
                            type="button"
                            className="media-modal__close"
                            onClick={closeModal}
                            aria-label="Закрыть"
                        >
                            ✕
                        </button>

                        <div className="media-modal__nav">
                            <button
                                type="button"
                                className="media-modal__nav-btn"
                                onClick={handlePrevReport}
                            >
                                ‹
                            </button>
                            <div className="media-modal__nav-info">
                                Кадр {modalMedia.index + 1} из{' '}
                                {modalMedia.reports.length}
                            </div>
                            <button
                                type="button"
                                className="media-modal__nav-btn"
                                onClick={handleNextReport}
                            >
                                ›
                            </button>
                        </div>

                        <div className="media-modal__content">
                            {currentReport.type === 'video' ? (
                                <video
                                    src={currentReport.url}
                                    controls
                                    autoPlay
                                />
                            ) : (
                                <img
                                    src={currentReport.url}
                                    alt="Медиа отчёта"
                                />
                            )}
                        </div>
                        <div className="media-modal__meta">
                            {modalMedia.screenAddress && (
                                <div className="media-modal__meta-line">
                                    <span>Адрес:</span>
                                    <span>
                                        {modalMedia.screenAddress}
                                        {modalMedia.city
                                            ? `, ${modalMedia.city}`
                                            : ''}
                                    </span>
                                </div>
                            )}
                            {currentReport.createdAt && (
                                <div className="media-modal__meta-line">
                                    <span>Дата:</span>
                                    <span>
                                        {new Date(
                                            currentReport.createdAt,
                                        ).toLocaleString('ru-RU')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvertisersSimplePage;
